import { Divider, Grid, Image } from 'semantic-ui-react'
import { useParams } from 'react-router-dom'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import {
  DEDUCTIONS_AND_CREDITS_SCREENS,
  DeductionsAndCreditsStepProps,
} from '.'
import {
  Button,
  GridRowColumn,
  Icon,
  Text,
} from '@/componentsV1/BaseComponents'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import ReviewSubSection, {
  displayResponse,
} from '../../shared/ReviewSubSection'
import { businessMileageQuestionIds } from './HadBusinessMileagePanel'
import { ownedFarmQuestionIds } from './OwnedFarmPanel'
import { charitableContributionsQuestionIds } from './CharitableContributionsPanel'
import { deductionsAndCreditsQuestionIds } from './DeductionsAndCreditsPanel'
import { ownedHomeQuestionIds } from './OwnedHomePanel'
import { educationQuestionIds } from './EducationPanel'
import { dependentCareQuestionIds } from './DependentCareExpensesPanel'
import {
  directHomeOfficeStandardQuestionIds,
  homeOfficeSimplifiedQuestionIds,
  homeOfficeStandardQuestionIds,
  indirectHomeOfficeStandardQuestionIds,
} from './HomeOfficePanel'
import { homeOfficeStandardStep1QuestionIds } from './HomeOfficeStandardStep1Panel'
import { useSetScreen } from '@/components/form-flow'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectTaxListQuestionResponsesByFormId,
  selectTaxListQuestionsByIds,
} from '../../taxChecklist.selectors'
import { TaxListOptionId, TaxListQuestionId } from '../../service'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useNavigateWithLocation } from '@/utils/routeHelpers'
import { selectTqFormIsReadOnly } from '../../../annualTaxFilings.selector'
import CommentArea from '../../../components/comment-area'
import { useReviewPanelComment } from '../../../helpers'
import { SubStepIdentifiers } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/stepProgress.helpers'
import { tqBigSpace } from '@/features/taxes/annual-taxes/tax-checklist/helpers'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'

const DeductionsAndCreditsReviewPanel = ({
  goBack,
  previousScreen,
}: DeductionsAndCreditsStepProps) => {
  const navigate = useNavigateWithLocation()
  const { formId } = useParams()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const readOnly = useReselector(selectTqFormIsReadOnly, formId)
  const isScorp = useReselector(selectIsCurrentUserScorp)

  const { setReviewScreen } = useSetScreen()
  const { comment, saveComment, updateComment } = useReviewPanelComment(
    SubStepIdentifiers.deductionsAndCredits
  )

  //the design for standard method step 2 is slightly different than the other review subsections, so we need
  //to create a bespoke review sub section
  const homeOfficeStandardQuestions = useReselector(
    selectTaxListQuestionsByIds,
    homeOfficeStandardQuestionIds
  )

  const totalAmountQuestion = Object.values(homeOfficeStandardQuestions).find(
    ({ question }) =>
      question?.id ===
      TaxListQuestionId.home_office_total_standard_amount_in_cents
  )

  const indirectExpenseQuestions = Object.values(
    homeOfficeStandardQuestions
  ).filter(
    ({ question }) =>
      question && indirectHomeOfficeStandardQuestionIds.includes(question.id)
  )

  const directExpenseQuestions = Object.values(
    homeOfficeStandardQuestions
  ).filter(
    ({ question }) =>
      question && directHomeOfficeStandardQuestionIds.includes(question.id)
  )

  const responseHomeOfficeMethod = useReselector(
    selectTaxListQuestionResponsesByFormId,
    TaxListQuestionId.home_office_deduction_method,
    Number(formId)
  )?.[0]?.value

  const ownedFarmResponse = useReselector(
    selectTaxListQuestionResponsesByFormId,
    TaxListQuestionId.owned_farm,
    Number(formId)
  )?.[0]?.value

  const ownedHomeResponse = useReselector(
    selectTaxListQuestionResponsesByFormId,
    TaxListQuestionId.owned_home,
    Number(formId)
  )?.[0]?.value

  return (
    <Grid>
      <GridRowColumn centerContent {...tqBigSpace}>
        <Image src="https://heard-images.s3.amazonaws.com/assets/magnifying-glass-checkmark.svg" />
      </GridRowColumn>
      <GridRowColumn {...tqBigSpace}>
        <Text as="display2" textAlign="center">
          Review
        </Text>
      </GridRowColumn>
      {!readOnly && (
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            Review your answers for accuracy. Click edit to go back to the
            previous section.
          </Text>
        </GridRowColumn>
      )}
      {readOnly && (
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            You can no longer edit your Personal Tax Questionnaire responses,
            but you can view them anytime.
          </Text>
        </GridRowColumn>
      )}
      <Grid.Row />
      <ReviewSubSection
        title="deductions and credits"
        // Worked from home should hide for scorps
        questionIds={deductionsAndCreditsQuestionIds.filter(
          (id) => !isScorp || id !== TaxListQuestionId.worked_from_home
        )}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.deductionsAndCredits}
      />
      {ownedHomeResponse && (
        <ReviewSubSection
          title="home owner"
          questionIds={ownedHomeQuestionIds}
          editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.ownedHome}
        />
      )}
      <ReviewSubSection
        title={'home office'}
        questionIds={[TaxListQuestionId.home_office_deduction_method]}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.homeOffice}
      />
      {responseHomeOfficeMethod ===
        TaxListOptionId.home_office_method_simplified && (
        <ReviewSubSection
          title="home office: simplified method"
          questionIds={homeOfficeSimplifiedQuestionIds}
          editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.homeOfficeSimplified}
        />
      )}
      {responseHomeOfficeMethod ===
        TaxListOptionId.home_office_method_standard && (
        <>
          <ReviewSubSection
            title="home office: standard method step 1"
            questionIds={homeOfficeStandardStep1QuestionIds}
            editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.homeOfficeStandardStep1}
          />
          {totalAmountQuestion?.responses?.[0] && (
            <>
              <Grid.Row className="short" style={{ justifyContent: 'center' }}>
                <Grid.Column width={4}>
                  <Text as="eyebrow">
                    {'home office: standard method step 2'}
                  </Text>
                </Grid.Column>
                <Grid.Column
                  width={4}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="link"
                    onClick={() =>
                      setReviewScreen(
                        DEDUCTIONS_AND_CREDITS_SCREENS.homeOfficeStandardStep2
                      )
                    }
                  >
                    <Icon size="1x" icon={faPen} style={{ marginRight: 4 }} />
                    Edit
                  </Button>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row className="short" />
              {totalAmountQuestion && (
                <Grid.Row
                  className="short"
                  key={totalAmountQuestion.question?.id}
                  style={{ justifyContent: 'center', alignItems: 'center' }}
                >
                  <Grid.Column width={4}>
                    {totalAmountQuestion.question?.text && (
                      <Text>{totalAmountQuestion.question?.text}</Text>
                    )}
                  </Grid.Column>
                  <Grid.Column
                    key={totalAmountQuestion.responses?.[0].id}
                    width={4}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      height: '100%',
                    }}
                  >
                    <Text as="bodyMd">
                      {totalAmountQuestion &&
                        displayResponse(
                          totalAmountQuestion.question,
                          totalAmountQuestion.options,
                          totalAmountQuestion.responses?.[0]
                        )}
                    </Text>
                  </Grid.Column>
                  {totalAmountQuestion.responses.length === 0 && (
                    <Grid.Column
                      key={`${totalAmountQuestion.question?.id}-no-response`}
                      width={4}
                    />
                  )}
                </Grid.Row>
              )}
              <GridRowColumn short {...tqBigSpace}>
                <Text as="eyebrow" color="darkGray">
                  {taxYear} INDIRECT EXPENSES
                </Text>
              </GridRowColumn>
              {indirectExpenseQuestions?.map(
                ({ question, responses, options }) => (
                  <Grid.Row
                    className="short"
                    key={question?.id}
                    style={{ justifyContent: 'center', alignItems: 'center' }}
                  >
                    <Grid.Column width={4}>
                      {question?.text && <Text>{question?.text}</Text>}
                    </Grid.Column>
                    {responses.map((response) => (
                      <Grid.Column
                        key={response.id}
                        width={4}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          height: '100%',
                        }}
                      >
                        <Text>
                          {displayResponse(question, options, response)}
                        </Text>
                      </Grid.Column>
                    ))}
                    {responses.length === 0 && (
                      <Grid.Column
                        key={`${question?.id}-no-response`}
                        width={4}
                      />
                    )}
                  </Grid.Row>
                )
              )}
              <GridRowColumn short {...tqBigSpace}>
                <Text as="eyebrow" color="darkGray">
                  {taxYear} DIRECT EXPENSES
                </Text>
              </GridRowColumn>
              {directExpenseQuestions?.map(
                ({ question, responses, options }) =>
                  Boolean(responses.length) && (
                    <Grid.Row
                      className="short"
                      key={question?.id}
                      style={{ justifyContent: 'center', alignItems: 'center' }}
                    >
                      <Grid.Column width={4}>
                        {question?.text && <Text>{question?.text}</Text>}
                      </Grid.Column>
                      {responses.map((response) => (
                        <Grid.Column
                          key={response.id}
                          width={4}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            height: '100%',
                          }}
                        >
                          <Text as="bodyMd">
                            {displayResponse(question, options, response)}
                          </Text>
                        </Grid.Column>
                      ))}
                      {responses.length === 0 && (
                        <Grid.Column
                          key={`${question?.id}-no-response`}
                          width={4}
                        />
                      )}
                    </Grid.Row>
                  )
              )}
              <GridRowColumn {...tqBigSpace}>
                <Divider />
              </GridRowColumn>
            </>
          )}
        </>
      )}
      <ReviewSubSection
        title="business mileage"
        questionIds={businessMileageQuestionIds}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.businessMileage}
      />
      {ownedFarmResponse && (
        <ReviewSubSection
          title="farm"
          questionIds={ownedFarmQuestionIds}
          editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.farm}
        />
      )}
      <ReviewSubSection
        title="education"
        questionIds={educationQuestionIds}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.education}
      />
      <ReviewSubSection
        title="dependent care expense"
        questionIds={dependentCareQuestionIds}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.dependentCare}
        group="childcareProviderId"
      />
      <ReviewSubSection
        title="charitable contributions"
        questionIds={charitableContributionsQuestionIds}
        editScreen={DEDUCTIONS_AND_CREDITS_SCREENS.charitableContributions}
      />
      {!readOnly && (
        <CommentArea
          label="Add a comment (optional)"
          placeholder="Add a note or ask a question to provide additional context for your tax preparer"
          value={comment}
          onChange={updateComment}
        />
      )}

      {!readOnly && (
        <FormFlowFooter
          isSubmit
          onBack={() => goBack(previousScreen ?? null)}
          onForward={() => {
            saveComment()
            navigate('/taxes/annual/tax_checklist/')
          }}
        />
      )}
    </Grid>
  )
}

export default DeductionsAndCreditsReviewPanel
